import React from 'react';
import {
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Tooltip
} from '@material-ui/core';
import { API_URL } from '../../constants/api';
import CancelIcon from '@material-ui/icons/Cancel';
import EditIcon from '@material-ui/icons/Edit';
import SaveIcon from '@material-ui/icons/Save';
import DeleteIcon from '@material-ui/icons/Delete';
import { apiBatchRequest, apiPost } from '../../utils/api_service';
import { ShowToast, toastType } from '../../utils/toast-service';
import {
    isGridFormValid,
    IsSectionVisible,
    UpdateColumnWithConfiguration
} from '../../utils/helper';
import GridTable from '../common/Table/Table';
import { SECTIONS } from '../../constants/appConstants';
import { contactRoleColumns } from '../../constants/MockData';
import _ from 'lodash';
import ConfirmDialog from '../common/Dailogbox/confirmDailog';

export default class ConfigurationContactRoles extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            contactRolesData: [],
            isInAddMode: false,
            configuration: [],
            selectedEditRowId: undefined,
            updateData: undefined,
            contactRoleCol: _.cloneDeep(contactRoleColumns),
            showWarningMessage: false,
            confirmMessage: ''
        };
    }

    componentDidMount() {
        this.getContactRoles();
    }

    getContactRoles = () => {
        this._asyncRequest = apiBatchRequest([
            {
                url: API_URL.Users.GetContactRolesMapping,
                key: 'contactRolesData'
            }
        ])
            .then(({ contactRolesData }) => {
                this._asyncRequest = null;
                this.setState({
                    contactRolesData: contactRolesData.data || [],
                    configuration: contactRolesData.config || []
                });
            })
            .catch(() => {
                this._asyncRequest = null;
                ShowToast(
                    'Failed to load Contact Roles.',
                    toastType.ERROR, 5000, 500
                );
            });
    };

    isValidForm = () => {
        let isValid = true;
        const { updateData, contactRoleCol } = this.state;

        contactRoleCol.forEach((col) => {
            isGridFormValid(updateData[col.key], col, updateData);
            if (col.isInErrorState === true) {
                isValid = false;
            }
        });

        this.setState({ contactRoleCol }, this.forceUpdate());
        return isValid;
    };

    isDuplicateRole = () => {
        const { updateData, contactRolesData } = this.state;
        return contactRolesData.some(
            (x) =>
                (x.roleName || '').toLowerCase().trim() ===
                (updateData.roleName || '').toLowerCase().trim() &&
                x.contactRoleId !== updateData.contactRoleId
        );
    };

    saveRecord = () => {
        if (!this.isValidForm()) return;

        if (this.isDuplicateRole()) {
            ShowToast(
                'Role "' + this.state.updateData.roleName + '" already exists.',
                toastType.ERROR, 5000, 500
            );
            return;
        }

        const { updateData } = this.state;
        const params = {
            contactRoleId: updateData.contactRoleId || 0,
            roleName: (updateData.roleName || '').trim()
        };

        this.saveContactRole(params);
    };

    saveContactRole = (params) => {
        apiPost({
            url: API_URL.Users.SaveContactRole,
            params: {},
            postBody: params
        }).then(({ data: response }) => {
            if (response.success === true) {
                ShowToast(
                    this.state.isInAddMode
                        ? 'Role added successfully.'
                        : 'Role updated successfully.',
                    toastType.SUCCESS, 5000, 500
                );
                this.resetAndReload();
            } else {
                ShowToast(response.errorMessage, toastType.ERROR, 5000, 500);
            }
        });
    };

    deleteRecord = (cellInfo) => {
        this.setState({
            showWarningMessage: true,
            confirmMessage: 'Do you want to delete the role "' + cellInfo.roleName + '"?',
            updateData: cellInfo
        });
    };

    onClickHandle = (confirmed) => {
        if (confirmed) {
            const { updateData } = this.state;
            apiPost({
                url: API_URL.Users.DeleteContactRole,
                params: {},
                postBody: updateData.contactRoleId
            }).then(({ data: response }) => {
                if (response.success === true) {
                    ShowToast('Role deleted successfully.', toastType.SUCCESS, 5000, 500);
                    this.resetAndReload();
                } else {
                    ShowToast(response.errorMessage, toastType.ERROR, 5000, 500);
                }
            });
        } else {
            this.setState({ showWarningMessage: false, confirmMessage: '' });
        }
    };

    resetAndReload = () => {
        this.setState(
            {
                updateData: undefined,
                selectedEditRowId: undefined,
                isInAddMode: false,
                showWarningMessage: false,
                confirmMessage: ''
            },
            () => this.getContactRoles()
        );
    };

    cancelMode = () => {
        this.setState({
            selectedEditRowId: undefined,
            isInAddMode: false,
            updateData: undefined
        });
    };

    handleTableEditChange = (value, fieldName) => {
        const { contactRoleCol } = this.state;
        const param = { ...this.state.updateData };

        param[fieldName] = value;

        isGridFormValid(
            value,
            contactRoleCol.find((x) => x.key === fieldName),
            param
        );

        this.setState({ updateData: param, contactRoleCol }, () => {
            this.forceUpdate();
        });
    };

    addNewRecord = () => {
        if (this.state.isInAddMode) {
            ShowToast('You can add one row at a time!', toastType.WARNING, 5000, 500);
            return;
        }

        const newRow = {
            contactRoleId: 0,
            roleName: null
        };
        this.setState({
            isInAddMode: true,
            selectedEditRowId: { key: 'contactRoleId', value: 0 },
            updateData: newRow,
            contactRoleCol: _.cloneDeep(contactRoleColumns)
        });
    };

    renderActionColumn = (cellInfo) => {
        const isEditing =
            this.state.selectedEditRowId !== undefined &&
            cellInfo.contactRoleId === this.state.selectedEditRowId.value;

        if (isEditing) {
            return (
                <>
                    <Tooltip title="Save Record">
                        <IconButton size="small" onClick={this.saveRecord}>
                            <SaveIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Cancel">
                        <IconButton size="small" onClick={this.cancelMode}>
                            <CancelIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </>
            );
        }

        const canEdit = IsSectionVisible(
            SECTIONS.CONFIGURATION,
            SECTIONS.CONTACT_ROLES,
            SECTIONS.ADD_EDIT
        );

        if (!canEdit) return null;

        return (
            <>
                <Tooltip title="Edit Record">
                    <IconButton
                        size="small"
                        onClick={() => {
                            this.setState({
                                selectedEditRowId: {
                                    key: 'contactRoleId',
                                    value: cellInfo.contactRoleId
                                },
                                isInAddMode: false,
                                updateData: { ...cellInfo }
                            });
                        }}
                    >
                        <EditIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
                <Tooltip title="Delete">
                    <IconButton
                        size="small"
                        onClick={() => this.deleteRecord(cellInfo)}
                    >
                        <DeleteIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            </>
        );
    };

    render() {
        const {
            contactRolesData,
            configuration,
            isInAddMode,
            selectedEditRowId,
            updateData,
            contactRoleCol
        } = this.state;

        return (
            <>
                {/* Delete Confirmation Dialog */}
                {this.state.showWarningMessage && (
                    <ConfirmDialog
                        IsOpen={this.state.showWarningMessage}
                        Message={this.state.confirmMessage}
                        OnHandelClick={(isValid) => this.onClickHandle(isValid)}
                        AgreeText="Yes"
                        DisAgreeText="No"
                    />
                )}

                <Table
                    aria-labelledby="tableMenuTitle"
                    size="small"
                    aria-label="configuration table"
                >
                    <TableHead>
                        <TableRow>
                            <TableCell
                                key="configureHeader"
                                className="content-header"
                                colSpan={2}
                            >
                                <div className="content-header d-flex">
                                    <div
                                        className="col-xs-11"
                                        style={{
                                            display: 'inline-block',
                                            alignItems: 'center'
                                        }}
                                    >
                                        Configure Contact Roles
                                    </div>
                                </div>
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        <TableRow>
                            <TableCell>
                                <GridTable
                                    columns={UpdateColumnWithConfiguration(
                                        contactRoleCol,
                                        configuration,
                                        []
                                    )}
                                    data={contactRolesData}
                                    config={contactRoleCol}
                                    renderActionColumn={this.renderActionColumn}
                                    selectedEditRowId={selectedEditRowId}
                                    isInAddMode={isInAddMode}
                                    EditSource={[]}
                                    handleTableEditChange={this.handleTableEditChange}
                                    updateData={updateData}
                                    isInline={true}
                                    AddNewRecord={{
                                        action: this.addNewRecord,
                                        title: 'Add New Contact Role',
                                        visible: IsSectionVisible(
                                            SECTIONS.CONFIGURATION,
                                            SECTIONS.CONFIGURE_CONTACT_ROLES,
                                            SECTIONS.ADD_EDIT
                                        )
                                    }}
                                />
                            </TableCell>
                        </TableRow>
                    </TableBody>
                </Table>
            </>
        );
    }
}

// export default ConfigurationContactRoles;